import { useState } from 'react';
import { Dialog, DialogActions, DialogContent, Drawer, Fade, Paper } from '@mui/material';
import { useIntl } from 'react-intl';
import { DataGrid } from '@mui/x-data-grid';
import DoneIcon from '@mui/icons-material/Done';
import { format } from 'date-fns';
import DialogTitle from '@mui/material/DialogTitle';
import CancelIcon from '@mui/icons-material/Cancel';
import DialogContentText from '@mui/material/DialogContentText';
import {
  useBulkDeleteUsersMutation,
  useCreateUserMutation,
  useGetUsersQuery,
  useUpdateUserMutation
} from 'services/userApi';
import './users.css';
import UserDetails from 'pages/users/components/UserDetails';
import UserCustomToolbar from 'pages/users/components/UserCustomToolbar';
import { useNotification } from 'hook/useNotification';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { parseDate, SIMPLE_DATE_FORMAT } from 'utils/formatTime';

/**
 * Component for managing Users, including displaying a list of users, adding, updating,
 * and deleting users. Uses a DataGrid for listing the users and a Drawer for editing details.
 *
 * @component
 *
 * @example
 * <Users />
 *
 * @returns {React.Component} The Users management component.
 */
export default function Users() {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();

  const { data: users, error: usersError, isLoading: usersLoading } = useGetUsersQuery();
  const [bulkDeleteUser] = useBulkDeleteUsersMutation();
  const [createUser] = useCreateUserMutation();
  const [updateUser] = useUpdateUserMutation();

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectionModel, setSelectionModel] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [confirmCloseFormDialogOpen, setConfirmCloseFormDialogOpen] = useState(false);

  const processedUsers = (users || []).map((user) => ({
    ...user,
    creationDate: format(parseDate(user.creationDate), SIMPLE_DATE_FORMAT)
  }));
  const columns = [
    {
      field: 'id',
      headerName: msg({ id: `page.users.table.column.id` }),
      width: 50,
      disableColumnMenu: true
    },
    { field: 'login', headerName: msg({ id: `page.users.table.column.login` }), width: 150 },
    {
      field: 'firstname',
      headerName: msg({ id: `page.users.table.column.firstname` }),
      width: 150
    },
    { field: 'lastname', headerName: msg({ id: `page.users.table.column.lastname` }), width: 150 },
    {
      field: 'creationDate',
      headerName: msg({ id: `page.users.table.column.creationDate` }),
      width: 150
    },
    {
      field: 'disabled',
      headerName: msg({ id: `page.users.table.column.enabled` }),
      width: 70,
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }}
        >
          {params.value ? (
            <CancelIcon fontSize="large" color="error" />
          ) : (
            <DoneIcon fontSize="large" color="success" />
          )}
        </div>
      )
    },
    {
      field: 'description',
      headerName: msg({ id: `page.users.table.column.description` }),
      width: 200
    }
  ];

  const toggleDrawer = (open) => () => {
    if (isFormDirty) {
      setConfirmCloseFormDialogOpen(true);
    } else {
      setDetailDrawerOpen(open);
    }
  };

  const handleCloseDrawer = () => {
    setDetailDrawerOpen(false);
    setConfirmCloseFormDialogOpen(false);
  };

  const handleCancelClose = () => {
    setConfirmCloseFormDialogOpen(false);
  };

  const handleFieldChange = (dirty) => {
    setIsFormDirty(dirty);
  };

  const handleRowClick = (params) => {
    setSelectedUser(params.row);
    setSelectedRowId(params.id);
    setDetailDrawerOpen(true);
  };

  const handleSelectionModelChange = (newSelection) => {
    setSelectionModel(newSelection);
  };

  const handleRowSelectionModelChange = (rowSelectionModel) => {
    setRowSelectionModel(rowSelectionModel);
  };

  const handleAdd = () => {
    setSelectedUser(null);
    setDetailDrawerOpen(true);
  };

  const handleDelete = async () => {
    if (selectedRows) {
      bulkDeleteUser(selectedRows.map((row) => row.id))
        .unwrap()
        .then(() => {
          addNotification({
            type: 'success',
            title: msg({ id: 'notification.success.title' }),
            message: msg({ id: 'page.users.delete.success' })
          });
          clearUserSelected();
        })
        .catch((error) => {
          console.error(error);
          addNotification({
            type: 'error',
            title: msg({ id: 'notification.error.title' }),
            message: msg({ id: 'page.users.delete.error' })
          });
        });
    }
  };

  const handleFormSubmit = (data) => {
    if (!data.id) {
      handleCreate(data);
    } else {
      handleUpdate(data);
    }
    setDetailDrawerOpen(false);
  };

  const selectedRows = rowSelectionModel.map((id) => users.find((user) => user.id === id));

  if (usersError)
    try {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.users.fetch.error' })
      });
    } catch {
      // Ignore exception
    }

  return (
    <Fade in timeout={(1000, 500)}>
      <MDBox flex={1} display="flex" sx={{ height: '100%' }} id="userContainer">
        <Paper
          elevation={2}
          sx={{ flex: 1, borderRadius: '10px', p: 2, mb: 1, overflow: 'hidden' }}
        >
          <DataGrid
            rows={processedUsers}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 20 } },
              selection: { ids: users && users.length > 0 ? [users[0].id] : [] },
              density: 'compact',
              sorting: {
                sortModel: [{ field: 'lastname', sort: 'asc' }]
              }
            }}
            slots={{ toolbar: UserCustomToolbar }}
            slotProps={{
              toolbar: {
                onAdd: handleAdd,
                onDelete: handleDelete,
                selectedRows
              }
            }}
            loading={usersLoading}
            pageSizeOptions={[20]}
            disableColumnResize
            checkboxSelection
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            selectionModel={selectionModel}
            onSelectionModelChange={handleSelectionModelChange}
            getRowClassName={(params) => (params.id === selectedRowId ? 'Mui-selected' : '')}
          />
        </Paper>
        <Drawer
          variant="temporary"
          anchor="right"
          open={detailDrawerOpen}
          onClose={toggleDrawer(false)}
          ModalProps={{ keepMounted: false }}
          PaperProps={{
            sx: { width: 700, p: 2 }
          }}
        >
          <UserDetails
            user={selectedUser}
            onSubmit={handleFormSubmit}
            onFieldChange={handleFieldChange}
          />
        </Drawer>
        <Dialog open={confirmCloseFormDialogOpen} onClose={handleCancelClose}>
          <DialogTitle>{msg({ id: 'dialog.unsaved_changes.title' })}</DialogTitle>
          <DialogContent>
            <DialogContentText>{msg({ id: 'dialog.unsaved_changes.message' })}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCancelClose} color="secondary">
              {msg({ id: 'button.cancel' })}
            </MDButton>
            <MDButton onClick={handleCloseDrawer} color="primary">
              {msg({ id: 'button.discard' })}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </Fade>
  );

  function clearUserSelected() {
    setSelectionModel([]);
    setRowSelectionModel([]);
    setSelectedUser(null);
    setSelectedRowId(null);
  }

  function handleCreate(data) {
    createUser(getUserRequest(data))
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.users.create.success' })
        });
        clearUserSelected();
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.users.create.error' })
        });
      });
  }

  function getUserRequest(data) {
    return {
      ...data,
      roles: data.roles?.map((role) => role?.id),
      password: data.password !== 'fake_password' ? data.password : ''
    };
  }

  function handleUpdate(data) {
    updateUser({
      userId: data.id,
      userRequest: getUserRequest(data)
    })
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.users.update.success' })
        });
        clearUserSelected();
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.users.update.error' })
        });
      });
  }
}
